import { c as _c } from "react/compiler-runtime";
import type { openai } from '@ai-sdk/openai';
import { Button, Dialog, DialogContent, DialogTitle } from '@newfront-insurance/core-ui/v2';
import { useAnalytics } from '@newfront-insurance/data-layer-client';
import type { ToolUIPart, UIDataTypes, UIMessagePart, UIToolInvocation, UITools } from 'ai';
import { Download, Maximize2 } from 'lucide-react';
import { useCallback, useMemo, useState } from 'react';
function isToolPart(part: UIMessagePart<UIDataTypes, UITools>): part is ToolUIPart {
  return part.type.startsWith('tool-');
}
interface ImageGenerationToolProps {
  part: ToolUIPart;
  messageUuid: string;
}
type ImageGenerationToolInvocation = UIToolInvocation<ReturnType<typeof openai.tools.imageGeneration>>;

/**
 * Component for rendering image_generation tool output.
 */
export function ImageGenerationTool(t0) {
  const $ = _c(3);
  const {
    part,
    messageUuid
  } = t0;
  if (!isToolPart(part)) {
    return null;
  }
  if (part.state === "output-available") {
    const t1 = part as ImageGenerationToolInvocation;
    let t2;
    if ($[0] !== messageUuid || $[1] !== t1) {
      t2 = <ImageGenerationView invocation={t1} messageUuid={messageUuid} />;
      $[0] = messageUuid;
      $[1] = t1;
      $[2] = t2;
    } else {
      t2 = $[2];
    }
    return t2;
  }
  return null;
}
interface ImageGenerationViewProps {
  invocation: ImageGenerationToolInvocation;
  messageUuid: string;
}
function ImageGenerationView(t0) {
  const $ = _c(39);
  const {
    invocation,
    messageUuid
  } = t0;
  const [isOpen, setIsOpen] = useState(false);
  const analytics = useAnalytics();
  invocation.output?.result;
  let t1;
  bb0: {
    const base64 = invocation.output?.result;
    if (!base64) {
      let t2;
      if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
        t2 = {
          imageSrc: null as string | null,
          filename: null as string | null
        };
        $[0] = t2;
      } else {
        t2 = $[0];
      }
      t1 = t2;
      break bb0;
    }
    const src = base64.startsWith("data:") ? base64 : `data:image/png;base64,${base64}`;
    let t2;
    if ($[1] !== src) {
      t2 = {
        imageSrc: src,
        filename: "generated-image.png"
      };
      $[1] = src;
      $[2] = t2;
    } else {
      t2 = $[2];
    }
    t1 = t2;
  }
  const {
    imageSrc,
    filename
  } = t1;
  let t2;
  if ($[3] !== analytics || $[4] !== filename || $[5] !== imageSrc || $[6] !== invocation.toolCallId || $[7] !== messageUuid) {
    t2 = () => {
      if (!imageSrc || !filename) {
        return;
      }
      analytics.track({
        event: "Image Generation Downloaded",
        properties: {
          toolCallId: invocation.toolCallId,
          messageUuid,
          filename
        }
      });
      const link = document.createElement("a");
      link.href = imageSrc;
      link.download = filename;
      link.click();
    };
    $[3] = analytics;
    $[4] = filename;
    $[5] = imageSrc;
    $[6] = invocation.toolCallId;
    $[7] = messageUuid;
    $[8] = t2;
  } else {
    t2 = $[8];
  }
  const handleDownload = t2;
  let t3;
  if ($[9] !== analytics || $[10] !== filename || $[11] !== invocation.toolCallId || $[12] !== messageUuid) {
    t3 = () => {
      analytics.track({
        event: "Image Generation Viewed Fullscreen",
        properties: {
          toolCallId: invocation.toolCallId,
          messageUuid,
          filename
        }
      });
      setIsOpen(true);
    };
    $[9] = analytics;
    $[10] = filename;
    $[11] = invocation.toolCallId;
    $[12] = messageUuid;
    $[13] = t3;
  } else {
    t3 = $[13];
  }
  const handleOpenFullscreen = t3;
  switch (invocation.state) {
    case "input-available":
      {
        let t4;
        if ($[14] === Symbol.for("react.memo_cache_sentinel")) {
          t4 = <div>Generating image...</div>;
          $[14] = t4;
        } else {
          t4 = $[14];
        }
        return t4;
      }
    case "output-available":
      {
        if (!imageSrc) {
          let t4;
          if ($[15] === Symbol.for("react.memo_cache_sentinel")) {
            t4 = <div>Unable to load generated image.</div>;
            $[15] = t4;
          } else {
            t4 = $[15];
          }
          return t4;
        }
        let t4;
        if ($[16] !== imageSrc) {
          t4 = <img src={imageSrc} alt="Generated by AI" className="max-h-48 w-auto max-w-full rounded-sm object-contain" />;
          $[16] = imageSrc;
          $[17] = t4;
        } else {
          t4 = $[17];
        }
        let t5;
        if ($[18] === Symbol.for("react.memo_cache_sentinel")) {
          t5 = <Maximize2 size={16} aria-hidden={true} />;
          $[18] = t5;
        } else {
          t5 = $[18];
        }
        let t6;
        if ($[19] !== handleOpenFullscreen) {
          t6 = <Button variant="secondary" size="small" className="gap-1" onClick={handleOpenFullscreen} data-testid="image-generation-open">{t5}View full screen</Button>;
          $[19] = handleOpenFullscreen;
          $[20] = t6;
        } else {
          t6 = $[20];
        }
        let t7;
        if ($[21] === Symbol.for("react.memo_cache_sentinel")) {
          t7 = <Download size={16} aria-hidden={true} />;
          $[21] = t7;
        } else {
          t7 = $[21];
        }
        let t8;
        if ($[22] !== handleDownload) {
          t8 = <Button variant="secondary" size="small" className="gap-1" onClick={handleDownload} data-testid="image-generation-download">{t7}Download</Button>;
          $[22] = handleDownload;
          $[23] = t8;
        } else {
          t8 = $[23];
        }
        let t9;
        if ($[24] !== t6 || $[25] !== t8) {
          t9 = <div className="flex flex-row items-center gap-2">{t6}{t8}</div>;
          $[24] = t6;
          $[25] = t8;
          $[26] = t9;
        } else {
          t9 = $[26];
        }
        let t10;
        if ($[27] !== t4 || $[28] !== t9) {
          t10 = <div className="bg-muted/20 inline-flex w-fit max-w-full flex-col items-start gap-2 rounded-md border p-3">{t4}{t9}</div>;
          $[27] = t4;
          $[28] = t9;
          $[29] = t10;
        } else {
          t10 = $[29];
        }
        let t11;
        if ($[30] === Symbol.for("react.memo_cache_sentinel")) {
          t11 = <DialogTitle className="sr-only">Generated Image Fullscreen View</DialogTitle>;
          $[30] = t11;
        } else {
          t11 = $[30];
        }
        let t12;
        if ($[31] !== imageSrc) {
          t12 = <DialogContent className="bg-background inline-grid max-h-[90vh] w-auto max-w-[90vw] place-items-center p-0">{t11}<img src={imageSrc} alt="Generated by AI" className="block max-h-[90vh] max-w-[90vw] object-contain" /></DialogContent>;
          $[31] = imageSrc;
          $[32] = t12;
        } else {
          t12 = $[32];
        }
        let t13;
        if ($[33] !== isOpen || $[34] !== t12) {
          t13 = <Dialog open={isOpen} onOpenChange={setIsOpen}>{t12}</Dialog>;
          $[33] = isOpen;
          $[34] = t12;
          $[35] = t13;
        } else {
          t13 = $[35];
        }
        let t14;
        if ($[36] !== t10 || $[37] !== t13) {
          t14 = <div className="flex flex-col items-start gap-2">{t10}{t13}</div>;
          $[36] = t10;
          $[37] = t13;
          $[38] = t14;
        } else {
          t14 = $[38];
        }
        return t14;
      }
    default:
      {
        return null;
      }
  }
}